/**
 * ClaimDocumentViewer
 * Fetches a claim document via the authenticated axios client, then renders
 * it in a modal overlay (PDF → <iframe>, image → <img>, other → download).
 */
import { useEffect, useRef, useState } from "react";
import { Download, FileText, Loader2, Paperclip, X } from "lucide-react";
import * as claimApi from "../api";
import type { ClaimDocument } from "../types";

type Phase =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; objectUrl: string; contentType: string; filename: string };

function guessExt(ct: string) {
  if (ct.includes("pdf"))  return ".pdf";
  if (ct.includes("png"))  return ".png";
  if (ct.includes("jpeg") || ct.includes("jpg")) return ".jpg";
  if (ct.includes("gif"))  return ".gif";
  return "";
}

function fmtBytes(n?: number | null) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

type Props = {
  claimId: number;
  doc: ClaimDocument;
  compact?: boolean;
  onOpen?: () => void; // called once the document successfully opens
};

export function ClaimDocumentViewer({ claimId, doc, compact = false, onOpen }: Props) {
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => { if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current); };
  }, []);

  async function open() {
    if (phase.kind === "loading") return;
    setPhase({ kind: "loading" });
    try {
      const { blob, contentType } = await claimApi.downloadClaimDocument(claimId, doc.id);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const objectUrl = URL.createObjectURL(blob);
      objectUrlRef.current = objectUrl;
      const filename = doc.originalFileName || `document${guessExt(contentType)}`;
      setPhase({ kind: "ready", objectUrl, contentType, filename });
      onOpen?.();
    } catch (err) {
      setPhase({ kind: "error", message: err instanceof Error ? err.message : "Failed to load document" });
    }
  }

  function close() { setPhase({ kind: "idle" }); }

  function download() {
    if (phase.kind !== "ready") return;
    const a = document.createElement("a");
    a.href = phase.objectUrl;
    a.download = phase.filename;
    a.click();
  }

  const isPdf   = (ct: string) => ct.includes("pdf");
  const isImage = (ct: string) => ct.startsWith("image/");

  return (
    <>
      {compact ? (
        <button
          type="button"
          onClick={open}
          disabled={phase.kind === "loading"}
          title={doc.originalFileName}
          className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
        >
          {phase.kind === "loading"
            ? <Loader2 className="h-3 w-3 animate-spin" />
            : <FileText className="h-3 w-3" />}
          {phase.kind === "loading" ? "Loading…" : "View"}
        </button>
      ) : (
        <div className="flex items-center gap-3 rounded-xl border bg-slate-50 px-3 py-2.5">
          <Paperclip className="h-4 w-4 shrink-0 text-slate-400" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-bold text-slate-800">{doc.originalFileName}</div>
            <div className="text-xs text-slate-400">
              {doc.documentType && <span className="mr-2">{doc.documentType}</span>}
              {fmtBytes(doc.fileSizeBytes)}
            </div>
          </div>
          <button
            type="button"
            onClick={open}
            disabled={phase.kind === "loading"}
            className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
          >
            {phase.kind === "loading"
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <FileText className="h-3 w-3" />}
            {phase.kind === "loading" ? "Loading…" : "View"}
          </button>
        </div>
      )}

      {phase.kind === "error" && (
        <p className="mt-1 text-xs font-semibold text-red-600">
          {phase.message}{" "}
          <button type="button" onClick={() => setPhase({ kind: "idle" })} className="underline">
            Dismiss
          </button>
        </p>
      )}

      {phase.kind === "ready" && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <div
            className="relative flex w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            style={{ maxHeight: "90vh" }}
          >
            <div className="flex shrink-0 items-center justify-between border-b bg-slate-50 px-5 py-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Paperclip className="h-4 w-4 text-slate-400" />
                {phase.filename}
                <span className="text-xs font-normal text-slate-400">({phase.contentType})</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={download}
                  className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                >
                  <Download className="h-3.5 w-3.5" /> Download
                </button>
                <button
                  type="button"
                  onClick={close}
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-slate-100">
              {isPdf(phase.contentType) ? (
                <iframe
                  src={phase.objectUrl}
                  title="Document preview"
                  className="h-full min-h-[70vh] w-full border-0"
                />
              ) : isImage(phase.contentType) ? (
                <div className="flex items-center justify-center p-6">
                  <img
                    src={phase.objectUrl}
                    alt={phase.filename}
                    className="max-h-[75vh] max-w-full rounded-xl object-contain shadow"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-4 py-16 text-slate-500">
                  <FileText className="h-12 w-12 text-slate-300" />
                  <p className="text-sm">Preview not available for this file type.</p>
                  <button
                    type="button"
                    onClick={download}
                    className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800"
                  >
                    <Download className="h-4 w-4" /> Download File
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
